import { useLayoutEffect } from 'react';
import { throttle } from 'lodash';
import { NetworkStatus } from '@apollo/client';
import { handleFetchMore } from '@src/util/handleFetchMore';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { recordSignals } from '@src/graphql/signals/actions';
import { trackEvent } from '@src/metrics';
import {
  useGetSearchVerifiedUsersForFeaturedTalentsFeedQuery,
  VerifiedUsersSortOptionEnum,
  GetSearchVerifiedUsersForFeaturedTalentsFeedQuery,
  SignalEntityTypeEnum,
  SignalTypeEnum,
} from '../../../graphql/generated';
import { getSearchTags } from '../utils/searchTags';
import { QueryParams } from '../interfaces/queryParams.interface';
import { DateRange } from '../utils/interfaces/getSearchTags.interface';

export interface UseVerifiedTalentsParams {
  dateRange?: DateRange;
  excludedUserIds: string[];
  queryParams: QueryParams;
  recruiterCurrencyCode: string;
}

export interface UseVerifiedTalentsValue {
  feed: GetSearchVerifiedUsersForFeaturedTalentsFeedQuery['searchVerifiedUsers']['result'];
  fetchMore: () => void;
  hasMoreResults: boolean;
  isInitialLoading: boolean;
  isLoading: boolean;
}

export const FIRST_BATCH_LIMIT = 12;

export const useSearchVerifiedUsers = ({
  recruiterCurrencyCode,
  queryParams,
  dateRange,
  excludedUserIds,
}: UseVerifiedTalentsParams): UseVerifiedTalentsValue => {
  const { data, networkStatus, fetchMore } = useGetSearchVerifiedUsersForFeaturedTalentsFeedQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      paginationOptions: {
        limit: FIRST_BATCH_LIMIT,
      },
      recruiterCurrencyCode: recruiterCurrencyCode,
      searchParams: getSearchTags(queryParams, dateRange),
      sortedPaginationOptions: {
        sortBy: VerifiedUsersSortOptionEnum.JOINED_AT,
      },
    },
  });

  const pagination = data?.searchVerifiedUsers.pagination;
  const feed = data?.searchVerifiedUsers.result || [];

  const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading = networkStatus <= 2;
  const isLoading = isInitialLoading || isFetchMoreLoading;

  const loadMore = throttle(() => {
    if (!isLoading && pagination?.hasMoreResults) {
      handleFetchMore(fetchMore, {
        updateQuery: (
          previousResult: GetSearchVerifiedUsersForFeaturedTalentsFeedQuery,
          { fetchMoreResult }: { fetchMoreResult: GetSearchVerifiedUsersForFeaturedTalentsFeedQuery },
        ) => {
          if (!fetchMoreResult || !previousResult?.searchVerifiedUsers) {
            return previousResult;
          }

          const previousApplicants = previousResult.searchVerifiedUsers;

          const newResult = fetchMoreResult.searchVerifiedUsers.result;
          const pagination = fetchMoreResult.searchVerifiedUsers.pagination;

          trackEvent({
            context: MetricContext.FEATURED_APPLICANTS_VIEW,
            interaction: MetricAction.FETCH_MORE,
            payload: {
              itemType: 'Featured applicants feed',
              itemValue: [...previousApplicants.result, ...newResult].length,
            },
          });

          recordSignals({
            currentLimit: previousApplicants.pagination.limit || 0,
            items: feed.map(item => ({
              id: item.userVerification.id,
              type: SignalEntityTypeEnum.USER_VERIFICATION,
            })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          return {
            searchVerifiedUsers: {
              ...previousApplicants,
              pagination: {
                ...previousApplicants.pagination,
                hasMoreResults: pagination.hasMoreResults,
                limit: pagination.limit,
                maxCursor: pagination.maxCursor,
              },
              result: [...previousApplicants.result, ...newResult],
            },
          };
        },
        variables: {
          excludedUserIds: excludedUserIds,
          paginationOptions: {
            limit: 9,
            minCursor: pagination.maxCursor,
          },
        },
      });
    }
  }, 200);

  const checkScrollPositionAndLoadMore = () => {
    const {
      body: { scrollHeight },
    } = document;
    const { innerHeight, scrollY } = window;
    const distanceFromBottom = scrollHeight - innerHeight - scrollY;

    if (!isLoading && !isFetchMoreLoading && pagination?.hasMoreResults && distanceFromBottom < 100) {
      loadMore();
    }
  };

  useLayoutEffect(() => {
    window.addEventListener('scroll', checkScrollPositionAndLoadMore);

    return () => {
      window.removeEventListener('scroll', checkScrollPositionAndLoadMore);
      loadMore.cancel();
    };
  }, [isLoading, pagination]);

  return {
    feed: feed,
    fetchMore: loadMore,
    hasMoreResults: !!pagination?.hasMoreResults,
    isInitialLoading: isInitialLoading,
    isLoading: isLoading,
  };
};
